import { useAuth } from '../../../auth/AuthProvider';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAgendaTranslation as useTranslation } from '../translations';
import dayjs from 'dayjs';

import {
    getAppointmentClinicians,
    scheduleAppointment,
    getAppointmentPatients
} from '../../../services/appointments';
import { agendaTimezone } from '../agendaUtils';

const CreateAppointmentDialog = ({
    roleUuid,
    clinicUuid,
    defaultDoctor,
    initialDate,
    zone,
    onClose,
    onCreated
}) => {
    const { user, role } = useAuth();
    const actor = { user, role };
    const { t } = useTranslation();
    const client = useQueryClient();
    const submitting = useRef(false);
    const active = useRef(true);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const [patient, setPatient] = useState(null);
    const [doctor, setDoctor] = useState(defaultDoctor);
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [duration, setDuration] = useState(30);
    const [time, setTime] = useState(() => {
        const selected = dayjs.tz(`${initialDate}T09:00`, zone);
        return (
            selected.isAfter(dayjs()) ? selected : dayjs().tz(zone).add(30, 'minute')
        ).format('YYYY-MM-DDTHH:mm');
    });
    const [uncertain, setUncertain] = useState(false);
    const [verified, setVerified] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setSearchTerm(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);
    const patients = useInfiniteQuery({
        queryKey: ['clinic-agenda-patients', roleUuid, clinicUuid, searchTerm],
        initialPageParam: undefined,
        queryFn: ({ pageParam, signal }) => getAppointmentPatients(actor, {
            search: searchTerm, pageSize: 25, token: pageParam, signal
        }),
        getNextPageParam: (last, pages, _, tokens) => {
            if (
                !last.users.length ||
                !last.token ||
                tokens.includes(last.token) ||
                pages.reduce((total, page) => total + page.users.length, 0) >= last.count
            )
                return undefined;
            return last.token;
        },
        staleTime: 30000
    });
    const clinicians = useQuery({
        queryKey: ['clinic-agenda-clinicians', roleUuid, clinicUuid],
        queryFn: () => getAppointmentClinicians(actor),
        staleTime: 60000
    });
    const patientOptions = [
        ...new Map(
            (patients.data?.pages.flatMap((page) => page.users) || []).map((person) => [
                person.role_uuid,
                person
            ])
        ).values()
    ];
    const doctorOptions = (clinicians.data || []).filter(
        (person) => person.role === 'DOCTOR'
    );
    if (
        defaultDoctor &&
        !doctorOptions.some((person) => person.role_uuid === defaultDoctor.role_uuid)
    )
        doctorOptions.unshift(defaultDoctor);
    const invalidate = () => {
        client.invalidateQueries({ queryKey: ['clinic-agenda-appointments'] });
    };
    const create = useMutation({
        retry: false,
        mutationFn: (payload) => scheduleAppointment(actor, payload),
        onSuccess: () => {
            if (!active.current) return;
            invalidate();
            onCreated();
        },
        onError: () => {
            if (!active.current) return;
            invalidate();
            setUncertain(true);
            setVerified(false);
        }
    });
    const parsedTime =
        time && dayjs(time).isValid() ? dayjs.tz(time, agendaTimezone(zone)) : null;
    const validTime =
        parsedTime?.isValid() &&
        parsedTime.format('YYYY-MM-DDTHH:mm') === time &&
        parsedTime.isAfter(dayjs());
    const validDuration = Number.isInteger(Number(duration)) && Number(duration) > 0;
    const canSubmit =
        patient?.role_uuid &&
        doctor?.role_uuid &&
        validTime &&
        validDuration &&
        (!uncertain || verified);
    const submit = async (event) => {
        event.preventDefault();
        if (!canSubmit || submitting.current) return;
        submitting.current = true;
        try {
            await create.mutateAsync({
                patient,
                doctor,
                ts: parsedTime.toISOString(),
                slot_duration: Number(duration)
            });
        } catch {
            // The mutation shows the error; do not repeat a possibly committed POST.
        } finally {
            submitting.current = false;
        }
    };
    const renderPerson = (props, person) => {
        const optionProps = { ...props };
        delete optionProps.key;
        return (
            <Box component="li" key={person.role_uuid} {...optionProps}>
                <Box>
                    <Typography>{person.name}</Typography>
                    {person.email && (
                        <Typography variant="caption" color="text.secondary">
                            {person.email}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={create.isPending ? undefined : onClose}
            aria-labelledby="create-appointment-title"
        >
            <Box component="form" onSubmit={submit}>
                <DialogTitle id="create-appointment-title">
                    {t('agenda.create')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {create.isError && (
                            <Alert severity="error">{t('agenda.saveError')}</Alert>
                        )}
                        {uncertain && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={verified}
                                        disabled={create.isPending}
                                        onChange={(event) =>
                                            setVerified(event.target.checked)
                                        }
                                    />
                                }
                                label={t('agenda.verifiedNotCreated')}
                            />
                        )}
                        {patients.isError && (
                            <Alert
                                severity="error"
                                action={
                                    <Button onClick={() => patients.refetch()}>
                                        {t('agenda.retry')}
                                    </Button>
                                }
                            >
                                {t('agenda.patientsError')}
                            </Alert>
                        )}
                        <Autocomplete
                            options={patientOptions}
                            value={patient}
                            onChange={(_, value) => setPatient(value)}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'input' || reason === 'clear')
                                    setSearch(value);
                            }}
                            filterOptions={(options) => options}
                            getOptionLabel={(person) => person.name || ''}
                            isOptionEqualToValue={(option, value) =>
                                option.role_uuid === value.role_uuid
                            }
                            renderOption={renderPerson}
                            loading={patients.isFetching}
                            loadingText={t('agenda.loading')}
                            noOptionsText={t('agenda.noPatients')}
                            disabled={create.isPending}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('agenda.patient')}
                                    required
                                />
                            )}
                        />
                        {patients.hasNextPage && (
                            <Button
                                startIcon={<ExpandMore />}
                                disabled={patients.isFetching || create.isPending}
                                onClick={() =>
                                    patients.fetchNextPage({ cancelRefetch: false })
                                }
                            >
                                {t('agenda.morePatients')}
                            </Button>
                        )}
                        {clinicians.isError && (
                            <Alert
                                severity="error"
                                action={
                                    <Button onClick={() => clinicians.refetch()}>
                                        {t('agenda.retry')}
                                    </Button>
                                }
                            >
                                {t('agenda.doctorsError')}
                            </Alert>
                        )}
                        <Autocomplete
                            options={doctorOptions}
                            value={doctor}
                            onChange={(_, value) => setDoctor(value)}
                            getOptionLabel={(person) => person.name || ''}
                            isOptionEqualToValue={(option, value) =>
                                option.role_uuid === value.role_uuid
                            }
                            renderOption={renderPerson}
                            loading={clinicians.isPending}
                            loadingText={t('agenda.loading')}
                            noOptionsText={t('agenda.noDoctors')}
                            disabled={create.isPending}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('agenda.clinician')}
                                    required
                                />
                            )}
                        />
                        <TextField
                            label={t('agenda.dateTime')}
                            type="datetime-local"
                            value={time}
                            onChange={(event) => setTime(event.target.value)}
                            disabled={create.isPending}
                            required
                            InputLabelProps={{ shrink: true }}
                            error={!validTime}
                            helperText={!validTime ? t('agenda.futureTime') : undefined}
                        />
                        <TextField
                            label={t('agenda.duration')}
                            type="number"
                            value={duration}
                            onChange={(event) => setDuration(event.target.value)}
                            disabled={create.isPending}
                            required
                            inputProps={{ min: 1, step: 1 }}
                            error={!validDuration}
                            helperText={
                                !validDuration ? t('agenda.validDuration') : undefined
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={create.isPending}
                    >
                        {t('agenda.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={create.isPending || !canSubmit}
                    >
                        {t('agenda.create')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
CreateAppointmentDialog.propTypes = {
    roleUuid: PropTypes.string.isRequired,
    clinicUuid: PropTypes.string,
    defaultDoctor: PropTypes.shape({
        role_uuid: PropTypes.string.isRequired,
        name: PropTypes.string
    }),
    initialDate: PropTypes.string.isRequired,
    zone: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired
};
export default CreateAppointmentDialog;
